import { serveCorrectionMode } from '@didto/domain';
import { Button } from '@didto/ui';
import type { ActionOutcome, useDirectActions } from '@/app/directActions';
import type { useToast } from '@/app/toast';
import { useClock } from '@/app/useClock';
import type { AppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { hintOf } from '@/lib/orders';
import { approvalRequest, withGrantRefusal } from '../pos/approvals';
import type { Approval, ApprovalRequest, ReasonRequest } from '../pos/sheets';

/**
 * Taking a serve back (owner decision 5, ADR-022), shared by the Expo board, the POS and the Orders
 * page. Inside serve.undo_window_seconds anyone who serves undoes without a reason. After it a
 * reason is required: the cashier or the owner (serve.correct) corrects directly; a server gives
 * the reason and then a cashier or the owner approves on the same tablet (ADR-023). The approval
 * sheet opens straight after the reason, without a refused round trip first. The server re-checks
 * every rule; a stale client window only costs one extra prompt.
 */
export const SERVE_APPROVAL_TITLE = 'Cashier or owner approval';

type DirectRun = ReturnType<typeof useDirectActions>['run'];
type Toast = ReturnType<typeof useToast>['toast'];

export interface ServeCorrectionDeps {
  run: DirectRun;
  toast: Toast;
  setReason: (r: ReasonRequest | null) => void;
  setApproval: (a: ApprovalRequest | null) => void;
  /** `{ terminal_id }` for a browser session with a chosen terminal, `{}` for a device. */
  terminalPayload: { terminal_id?: string };
  isDevice: boolean;
  /** The actor holds serve.correct (the cashier or the owner at didto.). */
  canCorrect: boolean;
  undoWindow: number;
  onDone?: () => void;
}

export interface ServedRef {
  id: string;
  served_at: string | null;
}

export type CorrectServe = (t: ServedRef, label: string, nowMs: number) => void;

export function serveCorrection(d: ServeCorrectionDeps): CorrectServe {
  const payloadFor = (t: ServedRef, why?: string) => ({
    ticket_id: t.id,
    ...d.terminalPayload,
    ...(why ? { reason: why } : {}),
  });
  const send = (t: ServedRef, label: string, why?: string, a?: Approval): Promise<void> =>
    d
      .run(
        'ticket.unserve',
        { ...payloadFor(t, why), ...(a ?? {}) },
        t.id,
        why ? 'Correct' : 'Undo',
        { quiet: true },
      )
      .then((r: ActionOutcome) => {
        if (r.ok) {
          d.onDone?.();
          d.toast(
            r.result.reopened
              ? `${label} back at the pass; order reopened`
              : `${label} back at the pass`,
          );
          return;
        }
        if (r.unconfirmed) return;
        const hint = hintOf(r.error);
        // the window closed while the button was on screen: the same tap continues with a reason
        if (r.error.code === 'invalid_payload' && hint.requires === 'reason') {
          ask(t, label, !d.canCorrect);
          return;
        }
        if (r.error.code === 'forbidden' && hint.requires === 'serve.correct' && why) {
          approve(t, label, why, r.error);
          return;
        }
        d.toast(r.error.message, 'danger');
      });

  const approve = (t: ServedRef, label: string, why: string, refused?: AppError) => {
    if (!d.isDevice) {
      d.toast(
        'After the undo window a correction needs serve.correct; PIN approvals happen on an enrolled tablet.',
        'danger',
      );
      return;
    }
    d.setApproval(
      approvalRequest(
        'ticket.unserve',
        payloadFor(t, why),
        {
          title: SERVE_APPROVAL_TITLE,
          reason: refused
            ? withGrantRefusal(
                refused,
                `Correct ${label}: “${why}”. A cashier or the owner enters their PIN.`,
              )
            : `Correct ${label}: “${why}”. A cashier or the owner enters their PIN.`,
        },
        (a) => send(t, label, why, a),
      ),
    );
  };

  const ask = (t: ServedRef, label: string, needsApproval: boolean) =>
    d.setReason({
      title: `Correct ${label}`,
      label: 'Why is it coming back?',
      required: true,
      confirmLabel: needsApproval ? 'Continue to approval' : 'Take it back',
      danger: true,
      onConfirm: (why) => (needsApproval ? approve(t, label, why) : send(t, label, why)),
    });

  return (t, label, nowMs) => {
    const mode = serveCorrectionMode(t.served_at, nowMs, d.undoWindow, d.canCorrect);
    if (mode === 'undo') void send(t, label);
    else ask(t, label, mode === 'approval');
  };
}

/**
 * Undo inside the window, Correct… after it; the label follows the clock. Taking a serve back
 * needs the server now, so the button waits for the connection (ADR-037).
 */
export function CorrectServeButton({
  servedAt,
  undoWindow,
  canCorrect,
  disabled,
  label,
  testIdPrefix,
  onPress,
}: {
  servedAt: string | null;
  undoWindow: number;
  canCorrect: boolean;
  disabled?: boolean;
  /** What is being taken back, for the accessible name ("Kitchen", "Bar addition"). */
  label: string;
  testIdPrefix: string;
  onPress: (nowMs: number) => void;
}) {
  const now = useClock(1000);
  const online = useOnline();
  const undo = serveCorrectionMode(servedAt, now, undoWindow, canCorrect) === 'undo';
  return (
    <Button
      size="sm"
      variant="quiet"
      disabled={!online || disabled}
      onClick={() => onPress(Date.now())}
      data-testid={`${testIdPrefix}-${undo ? 'undo' : 'correct'}`}
      aria-label={`${undo ? 'undo' : 'correct'} ${label}`}
    >
      {undo ? 'Undo' : 'Correct…'}
    </Button>
  );
}
